import { type api, useInfiniteApiQuery } from '@vrplatform/data';
import type { Maybe } from '@vrplatform/utils';
import type { activeStatus_enum } from '@vrplatform/graphql';
import { useListingsTableFilter } from './ListingsTableFilter';

export type ListingTableQueryData = NonNullable<
  ReturnType<typeof useListingsTableQuery>['data']
>;

export type ListingRow = ListingTableQueryData['pages'][number]['list'][number];

type Filter = ReturnType<typeof useListingsTableFilter>['filter'];

export const STORYBOOK_LISTINGS_TABLE_KEY = 'listings-table';

export const getListingsTableQueryFilter = ({
  filter,
  page,
  limit = 50,
}: {
  filter: Filter;
  limit?: number;
  page: number;
}): NonNullable<api['/listings']['get']['parameters']['query']> => {
  const status = (filter.status as Maybe<activeStatus_enum>) ?? 'active';

  const isNotActiveOwnership = filter.ownerIds?.[0] === 'unmapped';

  return {
    limit,
    page,
    status,
    search: filter.search?.trim(),
    contactId: isNotActiveOwnership ? undefined : filter.ownerIds?.join(','),
    connectionId: filter.pmsConnectionId ?? undefined,
    activeOwnership: isNotActiveOwnership ? false : undefined,
    activeRecurringFeePeriods: filter.recurringFeeId ?? undefined,
  };
};

export const useListingsTableQuery = () => {
  const { filter } = useListingsTableFilter();

  const queryData = useInfiniteApiQuery(
    async ($api, signal, args, pageParam) => {
      const result = await $api.GET('/listings', {
        params: {
          query: getListingsTableQueryFilter({
            filter: args.filter,
            page: pageParam,
            limit: undefined,
          }),
        },
        signal,
      });

      if (result.error) throw result.error;

      const list = result.data?.data ?? [];

      return {
        list,
        aggregate: result.data?.pagination.total ?? 0,
        pagination: result.data?.pagination,
      };
    },
    {
      variables: {
        filter,
      },
      queryGroup: 'listings',
      uniqueQueryKey: 'listings-table',
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        return lastPage.pagination.nextPage ?? null; // return null if no more pages
      },
      storybookQueryKey: STORYBOOK_LISTINGS_TABLE_KEY,
    }
  );

  return queryData;
};
